import { Injectable } from '@angular/core';
import { UserStorageService } from './storage/user-storage.service';
import { Trip } from '../data-models/trip-models/trip.model';


enum ReportStatus {Incomplete, Awaiting, FOSRejected, FOSAccepted}

@Injectable({
  providedIn: 'root'
})
export class ConflictResolverService {

  constructor() { }


  static resolveConflicts(userDatabase: PouchDB.Database): Promise<boolean> {
    return new Promise<boolean>((res, rej) => {

      userDatabase.get('user', {conflicts: true}).then((doc: any) => {
        if (!doc._conflicts) {
          console.log("No conflicts on user doc");
          res(true);
        } else {
          userDatabase.get(doc._id, {open_revs: doc._conflicts}).then((rev_doc: any[]) => {
            console.log(rev_doc);
            let rev_user = rev_doc.reduce((curr, acc) => new Date(curr.lastUpdate ?? 0).toISOString() > new Date(acc.lastUpdate ?? 0).toISOString() ? curr : acc, doc);
            UserStorageService.saveUser(rev_user.user, true).catch(err => console.log(err)).then(() => {
              userDatabase.bulkDocs(rev_doc.map((x) => {x.ok._deleted = true; return x.ok;})).then(() => res(true));              
            });
          })
        }
      }).then(() => {
        UserStorageService.getCurrTrip().then(() => {
          let docID: string = 'trip-' + UserStorageService.currTrip.tripID;
          if (docID == 'trip-') {
            console.log("no active trip");
            res(true);
            return;
          }
          //Get the current trip with all conflicts present
          userDatabase.get(docID, {conflicts: true}).then((doc: any) => {
            if (!doc._conflicts) {
              console.log("no conflicts");
              res(true);
              return;
            }
            let baseTrip = Trip.transformAnonymous(doc.trip);
            console.log(baseTrip)
            userDatabase.get(doc._id, {open_revs: doc._conflicts}).then((rev_doc: any[]) => {
              console.log(rev_doc);
              let conflicting_trips: Trip[] = Trip.transformAnonArray(rev_doc.map((trip_doc: any) => trip_doc.ok));
              conflicting_trips.push(baseTrip);

              //Deletions are additive across revisions: the merge below keys reports purely by
              //internalId and takes the union, so without these tombstones a report the user
              //deleted on one revision is resurrected by any revision that still carries it
              let deletedIds: Set<string> = new Set<string>(
                conflicting_trips.reduce((ids: string[], t: Trip) => ids.concat(t.deletedReportIds ?? []), [])
              );
              baseTrip.deletedReportIds = Array.from(deletedIds);

              //Start, cancel, end reports
              baseTrip.startFishingReport = this.resolveOnSingleReport(conflicting_trips.map((t: Trip) => t.startFishingReport));
              baseTrip.cancelFishingReport = this.resolveOnSingleReport(conflicting_trips.map((t: Trip) => t.cancelFishingReport));
              baseTrip.endFishingReport = this.resolveOnSingleReport(conflicting_trips.map((t: Trip) => t.endFishingReport));
    
              //Catch, Pause, Offload reports
              baseTrip.dailyCatchReports = this.resolveOnMultiReports(conflicting_trips.map((t: Trip) => t.dailyCatchReports), deletedIds);
              baseTrip.pauseFishingReports = this.resolveOnMultiReports(conflicting_trips.map((t: Trip) => t.pauseFishingReports), deletedIds);
              baseTrip.offloadReports = this.resolveOnMultiReports(conflicting_trips.map((t: Trip) => t.offloadReports), deletedIds);
    
              console.log(baseTrip)
              UserStorageService.currTrip = baseTrip;
              // Save merged trip
              UserStorageService.saveTrip(baseTrip, true, true)
                .then(() =>
                  userDatabase.bulkDocs(rev_doc.map((trip_doc: any) => trip_doc.ok).map((x) => { x._deleted = true; return x; })))
                .then(() => res(true))
                .catch(() => res(false));
            });
    
          }).catch(function (err) {
            if (err.name == 'not_found') console.log('Active trip not found');
            else console.log(err);
            res(false);
          });
    
        });
      });
    });
  }

  //For reports for which there can only be one per trip -- Start, Cancel, End
  static resolveOnSingleReport(reports: any[], useLWW: boolean = false): any {
    return reports.reduce((curr, acc) => {
      let s1 = this.getReportStatus(curr.confirmationNumber);
      let s2 = this.getReportStatus(acc.confirmationNumber);
      //Take the version with an accepted FOS response
      if (s1 == ReportStatus.FOSAccepted) return curr;
      if (s2 == ReportStatus.FOSAccepted) return acc;
      //If both are rejected by FOS, take the version with the more recent submission
      if (s1 == ReportStatus.FOSRejected && s2 == ReportStatus.FOSRejected) return new Date(curr.submissionDate).toISOString() < new Date(acc.submissionDate).toISOString() ? acc : curr;
      //A fresh resubmission (Awaiting response) should replace a stale FOS rejection when it is newer.
      //Compare completionDate so a deliberate reopen+resubmit isn't reverted to the old error; ties keep the rejection.
      if (s1 == ReportStatus.FOSRejected && s2 == ReportStatus.Awaiting)
        return new Date(acc.completionDate ?? 0).getTime() > new Date(curr.completionDate ?? 0).getTime() ? acc : curr;
      if (s1 == ReportStatus.Awaiting && s2 == ReportStatus.FOSRejected)
        return new Date(curr.completionDate ?? 0).getTime() > new Date(acc.completionDate ?? 0).getTime() ? curr : acc;
      //Take one with any FOS response over one with none, or submitted one awaiting response over non-submitted
      //The use of enums allows us to do a comparison here
      if (s1 > s2) return curr;
      if (s2 > s1) return acc;

      //Take the complete version over the non-complete, use Last Write Wins if applicable
      if (curr.isComplete == acc.isComplete && useLWW) return new Date(curr.lastUpdate).toISOString() < new Date(acc.lastUpdate).toISOString() ? acc : curr;
      return curr.isComplete ? curr : acc;
    });
  }


  static resolveOnMultiReports(reports: any[], deletedIds: Set<string> = new Set<string>()) {
    let repMap: Map<string, any[]> = new Map();
    let finalReps = [];
    reports
    .reduce((accumulator, value) => accumulator.concat(value), [])
    .forEach((rep: any) => {
      if (deletedIds.has(rep.internalId)) return; // deleted by the user, don't bring it back
      if (repMap.has(rep.internalId)) repMap.set(rep.internalId, repMap.get(rep.internalId)!.concat([rep]));
      else repMap.set(rep.internalId, [rep]);
    });
    for (var cr of repMap.values()) finalReps.push(this.resolveOnSingleReport(cr, true));
    return finalReps;
  }


  static getReportStatus(confirmationNumber: string) {
    if (confirmationNumber == 'Incomplete') {
      return ReportStatus.Incomplete;
    } else if (confirmationNumber.toLowerCase().includes('awaiting response')) {
      return ReportStatus.Awaiting;
    } else if (confirmationNumber.substring(0, 3) == 'FOS') {
      return ReportStatus.FOSAccepted;
    } else {
      return ReportStatus.FOSRejected;
    }
  }
}
